import DocumentViewModal from "@/components/DocumentViewModal";
import DownloadFile from "@/components/DownloadFile";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import Toaster from "@/components/Toaster";
import { sendUserLoginDetails } from "@/store/slices/internalUsersSlice";
import { getProfileUpdateRequest, getProfileUpdateRequestAction } from "@/store/slices/customerSlice";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/solid";
import { Typography, Button } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const InfoField = ({ label, value, children }) => (
    <div className="py-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
        {children}
    </div>
);

const DocField = ({ label, value, docUrl, onView, t }) => (
    <div className="py-3">
        <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</p>
            {docUrl && (
                <span className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onView(docUrl)}
                        className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                        title={t('view')}
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <DownloadFile fileUrl={docUrl} className="text-gray-700 hover:text-blue-600" />
                </span>
            )}
        </div>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
    </div>
);

const Section = ({ title, children }) => (
    <div className="border-b border-gray-200 last:border-b-0 pb-6 last:pb-0">
        <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
            {children}
        </div>
    </div>
);

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return "—";
    }
}

// old/new
const DiffCard = ({ label, oldValue, newValue, isDoc = false, onView }) => {
    if (!oldValue && !newValue) return null;

    return (
        <div className="border rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase">{label}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs text-gray-500">Old Value</p>
                    {isDoc ? (
                        oldValue ? (
                            <button
                                type="button"
                                onClick={() => onView(oldValue)}
                                className="mt-1 text-sm font-medium text-blue-600 hover:underline break-all text-left"
                            >
                                View Document
                            </button>
                        ) : (
                            <p className="text-sm font-medium text-gray-400 mt-1">—</p>
                        )
                    ) : (
                        <p className="text-sm font-medium text-gray-900 mt-1">{oldValue || "—"}</p>
                    )}
                </div>

                <div className="bg-blue-50 rounded p-3">
                    <p className="text-xs text-blue-600">New Value</p>
                    {isDoc ? (
                        newValue ? (
                            <button
                                type="button"
                                onClick={() => onView(newValue)}
                                className="mt-1 text-sm font-medium text-blue-600 hover:underline break-all text-left"
                            >
                                View Document
                            </button>
                        ) : (
                            <p className="text-sm font-medium text-gray-400 mt-1">—</p>
                        )
                    ) : (
                        <p className="text-sm font-medium text-blue-900 mt-1">{newValue || "—"}</p>
                    )}
                </div>
            </div>
        </div>
    );
};



export function ViewCustomer({ type = "Customer" }) {
    const { t } = useTranslation("customer");
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const page_url = type?.toLowerCase();
    const [isShowLoader, setIsShowLoader] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documentUrl, setDocumentUrl] = useState("");
    const [updateRequestData, setUpdateRequestData] = useState(null);
    const [profileActionSuccess, setProfileActionSuccess] = useState(false);
    const user = useSelector((s) => s.customer.selected);
    const { loading } = useSelector((s) => s.customer);
    const { loading:internalUserLoader } = useSelector((s) => s.internalUser);
    // const updateRequestData = useSelector(
    //     (s) => s.customer.profileUpdateRequest
    // );

    // const handleProfileUpdateAction = (action) => {
    //     dispatch(
    //         getProfileUpdateRequestAction({
    //             customerId: id,
    //             action,
    //         })
    //     )
    //         .unwrap()
    //         .then((res) => {
    //             // success
    //              setProfileActionSuccess(true);
    //         })
    //         .catch((err) => {
    //             // error
    //         });
    // };

    const handleProfileUpdateAction = async (action) => {
    const isApprove = action === "approved";

    const confirmation = await Swal.fire({
        title: isApprove 
            ? t('are_you_sure') 
            : t('are_you_sure'),
        text: isApprove
            ? t('are_you_sure_you_want_to_approve_these_updates')
            : t('are_you_sure_you_want_to_reject_these_profile_changes'),
        icon: isApprove ? "question" : "warning",
        showCancelButton: true,
        confirmButtonColor: isApprove ? "#000000" : "#d33",
        cancelButtonColor: "#6B7280",
        confirmButtonText: isApprove 
            ? t('yes_approve') 
            : t('yes_reject'),
        cancelButtonText: t('cancel', 'Cancel')
    });

    if (!confirmation.isConfirmed) return;

    try {
        const response = await dispatch(
            getProfileUpdateRequestAction({
                customerId: id,
                action,
            })
        ).unwrap();

        Toaster.success(
            response?.message || 
            (isApprove ? t('request_approved_successfully', 'Request approved successfully') : t('request_rejected_successfully', 'Request rejected successfully'))
        );

        setProfileActionSuccess(true);
    } catch (err) {
        Toaster.error(
            err?.message || t('something_went_wrong', 'Something went wrong')
        );
    }
};

    useEffect(() => {
        setIsShowLoader(false);
    }, []);

    useEffect(() => {
    if (profileActionSuccess) {
        window.location.reload();
    }
    }, [profileActionSuccess]);

    useEffect(() => {
        setIsShowLoader(true);
        if (id) {
            dispatch(getProfileUpdateRequest(id))
            .unwrap()
            .then((res) => {
                // console.log("testt ", res);
                    if (res?.old_data) {
                        // console.log('res: ', res);
                        setUpdateRequestData(res);
                    }
                })
                .catch(() => {})
                .finally(() => setIsShowLoader(false));
        } else {
            setIsShowLoader(false);
        }
        // console.log('data Hello: ', updateRequestData);
    }, [dispatch, id]);

    const openDocument = (url) => {
        setDocumentUrl(url);
        setIsModalOpen(true);
    };

    if (!user && !loading && !isShowLoader) {
        return (
            <div className="mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ScrollToTop />
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Typography variant="h6" color="gray" className="mb-4">
                        {t('no_customer_data_found')}
                    </Typography>
                    <Link to={`/dashboard/${page_url}s`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium">
                        <ArrowLeftIcon className="h-4 w-4" />
                        {t('back_to_list')}
                    </Link>
                </div>
            </div>
        );
    }

    const handleSendLoginDetails = async (user_id) => {
        try {
            const confirmation = await Swal.fire({
                title: t('send_login_details_q'),
                text: t('send_login_details_text'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t('yes_send_it'),
                cancelButtonText: t('cancel')
            });

            if (!confirmation.isConfirmed) return;

            const response = await dispatch(sendUserLoginDetails(user_id)).unwrap();

            if (response?.status) {
                Toaster.success(
                    response?.message || t('login_details_sent_successfully')
                );
            } else {
                Toaster.error(
                    response?.message || t('failed_to_send_login_details')
                );
            }

        } catch (error) {
            Toaster.error(
                error?.message || t('something_went_wrong_sending_login_details')
            );
        }
    };
    
    // old/new
    const oldData = updateRequestData?.old_data || {};
    // console.log(updateRequestData);
    const updatedData = updateRequestData?.updated_data || {};

    return (
        <div className="mb-8 flex flex-col gap-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader || internalUserLoader) && <ShowLoader />}
            <ScrollToTop />

            {/* Header */}
            <div className="flex flex-col gap-4">
                <Link
                    to={`/dashboard/${page_url}s`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition w-fit"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('back')}
                </Link>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Typography variant="h5" color="blue-gray" className="font-bold text-gray-900">
                        {t('profile_details')}
                    </Typography>
                    <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        onClick={() => navigate(`/dashboard/${page_url}s/edit/${id}`)}
                        className="rounded-lg w-fit"
                    >
                        {t('edit_profile')}
                    </Button>
                </div>
            </div>

            {/* Profile Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Profile Header */}
                <div className="p-6 md:p-8 bg-gray-50/50 border-b border-gray-100">
                    
                    {/* Top Row */}
                    <div className="flex justify-between items-start">
                        
                        {/* Left Section */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <button
                                type="button"
                                onClick={() => user?.profile_image && openDocument(user.profile_image)}
                                className={`flex-shrink-0 rounded-full overflow-hidden shadow-md ${user?.profile_image ? "cursor-pointer ring-2 ring-offset-2 ring-transparent hover:ring-blue-200 transition" : ""}`}
                            >
                                <img
                                    src={user?.profile_image || "/img/default-avatar-profile-icon.avif"}
                                    alt={user?.first_name || t('profile')}
                                    className="w-24 h-24 object-cover"
                                />
                            </button>
    
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {user?.first_name} {user?.last_name || ""}
                                </h2>
                                <p className="text-sm font-medium text-gray-600 mt-0.5">
                                   {user?.role || "—"}
                                </p>
                                <p className="text-xs font-medium text-gray-500 mt-1">
                                    {/* {t('id', { id: '' })} {String(user?.id ?? "").padStart(4, "0")} */}
                                    ID {String(user?.id ?? "").padStart(4, "0")}
                                </p>
                            </div>
                        </div>
    
                        {/* Right Button */}
                        <div>
                            <Button
                                type="button"
                                onClick={() => handleSendLoginDetails(user?.id)}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
                            >
                                {t('send_login_details')}
                            </Button>
                        </div>
    
                    </div>
                </div>

                {/* Info Sections */}
                <div className="p-6 md:p-8 flex flex-col gap-6">
                    <Section title={t('contact_information')}>
                        <InfoField
                            label={t('phone_number')}
                            value={[user?.country_code, user?.mobile_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField
                            label={t('alternate_number')}
                            value={[user?.alternate_country_code, user?.alternate_number].filter(Boolean).join(" ") || null}
                        />
                        <InfoField label={t('email_address')} value={user?.email} />
                        <InfoField label={t('member_since')} value={formatDate(user?.date)} />
                        <InfoField label={t('gender')} value={user?.gender} />
                    </Section>

                    <Section title={t('company_and_address')}>
                        <DocField
                            label={t('id_proof')}
                            value={user?.residence_id}
                            docUrl={user?.residence_image}
                            onView={openDocument}
                            t={t}
                        />
                        <InfoField label={t('registered_company_name')} value={user?.company_name} />
                        <InfoField label={t('business_type')} value={user?.vendor_business_type_name} />
                        <DocField
                            label={t('national_address')}
                            value={user?.permanent_address}
                            docUrl={user?.address_proof_image}
                            onView={openDocument}
                            t={t}
                        />
                        <DocField
                            label={t('commercial_registration_certificate')}
                            value={user?.commercial_reg_number}
                            docUrl={user?.commercial_reg_image}
                            onView={openDocument}
                            t={t}
                        />
                        <DocField
                            label={t('vat_certificate_number_label')}
                            value={user?.vat_certificate_number}
                            docUrl={user?.vat_certificate_image}
                            onView={openDocument}
                            t={t}
                        />
                    </Section>

                    <Section title={t('banking_details')}>
                        <DocField
                            label={t('account_number_label')}
                            value={user?.account_number}
                            docUrl={user?.passbook_image}
                            onView={openDocument}
                            t={t}
                        />
                        <InfoField label={t('bank_iban_label')} value={user?.bank_swift_code} />
                        <InfoField label={t('account_holder_first_name_label')} value={user?.account_holder_first_name} />
                    </Section>

                    <Section title={t('other_documents_label')}>
                        {user?.other_documents_detail && (
                            <div className="py-3 sm:col-span-2 lg:col-span-3">
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{t('details')}</p>
                                <p className="text-sm font-medium text-gray-900">{user.other_documents_detail}</p>
                            </div>
                        )}
                        {user?.other_documents && user.other_documents.length > 0 ? (
                            user.other_documents.map((doc, index) => (
                                <div key={index} className="py-3 flex items-start gap-2">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                                            {doc?.document_name || t('document_index', { index: index + 1 })}
                                        </p>
                                        <span className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                title={t('view')}
                                                onClick={() => openDocument(doc?.file)}
                                                className="p-1.5 rounded-md hover:bg-gray-200 text-gray-700 hover:text-blue-600 transition"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <DownloadFile fileUrl={doc?.file} className="text-gray-700 hover:text-blue-600" />
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-3 sm:col-span-2 lg:col-span-3">
                                <p className="text-sm text-gray-500 italic">{t('no_documents_available')}</p>
                            </div>
                        )}
                    </Section>
                </div>
            </div>

        {/* for the old/new data of the customer starts here */}
            {updateRequestData &&
                updateRequestData.updated_data &&
                updateRequestData.old_data &&
                (() => {
                    const oldData = updateRequestData.old_data;
                    const updatedData = updateRequestData.updated_data;

                    // Helper to check if a value is valid (hides null, undefined, and empty strings)
                    const isValid = (val) => val !== null && val !== undefined && val !== "";

                    // Text fields (all under Personal Information)
                    // const textFields = [
                    //     ["first_name", "First Name"],
                    //     ["last_name", "Last Name"],
                    //     ["mobile_number", "Mobile Number"],
                    //     ["alternate_number", "Alternate Number"],
                    //     ["email", "Email"],
                    //     ["gender", "Gender"],
                    //     ["company_name", "Company Name"],
                    //     ["business_type", "Business Type"],
                    //     ["residence_id", "Residence ID"],
                    //     ["permanent_address", "Permanent Address"],
                    //     ["commercial_reg_number", "Commercial Reg. Number"],
                    //     ["vat_certificate_number", "VAT Certificate Number"],
                    //     ["bank_first_name", "Account Holder Name"],
                    //     ["bank_name", "Bank Name"],
                    //     ["account_number", "Account Number"],
                    //     ["bank_swift_code", "SWIFT Code"],
                    // ];
                    const textFields = [
                        ["first_name", t("first_name", "First Name")],
                        ["last_name", t("last_name", "Last Name")],
                        ["mobile_number", t("mobile_number", "Mobile Number")],
                        ["alternate_number", t("alternate_number", "Alternate Number")],
                        ["email", t("email", "Email")],
                        ["gender", t("gender", "Gender")],
                        ["company_name", t("company_name", "Company Name")],
                        ["business_type", t("business_type", "Business Type")],
                        ["residence_id", t("residence_id", "Residence ID")],
                        ["permanent_address", t("permanent_address", "Permanent Address")],
                        ["commercial_reg_number", t("commercial_reg_number", "Commercial Reg. Number")],
                        ["vat_certificate_number", t("vat_certificate_number", "VAT Certificate Number")],
                        ["bank_first_name", t("account_holder_name", "Account Holder Name")],
                        ["bank_name", t("bank_name", "Bank Name")],
                        ["account_number", t("account_number", "Account Number")],
                        ["bank_swift_code", t("swift_code", "SWIFT Code")],
                    ];

                    // Image fields (shown at the end)
                    // const imageFields = [
                    //     ["residence_image", "Residence Proof"],
                    //     ["vat_certificate_image", "VAT Certificate"],
                    //     ["commercial_reg_image", "Commercial Registration"],
                    //     ["address_proof_image", "Address Proof"],
                    //     ["passbook_image", "Passbook Document"],
                    // ];

                    const imageFields = [
                        ["residence_image", t("residence_proof", "Residence Proof")],
                        ["vat_certificate_image", t("vat_certificate", "VAT Certificate")],
                        ["commercial_reg_image", t("commercial_registration", "Commercial Registration")],
                        ["address_proof_image", t("address_proof", "Address Proof")],
                        ["passbook_image", t("passbook_document", "Passbook Document")],
                    ];

                    const hasPersonalInfo = textFields.some(
                        ([key]) => isValid(oldData[key]) || isValid(updatedData[key])
                    );

                    const hasImages = imageFields.some(
                        ([key]) => isValid(oldData[key]) || isValid(updatedData[key])
                    );

                    const hasOtherDocs =
                        Array.isArray(updatedData.other_documents) && updatedData.other_documents.length > 0;

                    // Hides entire card if no actual data exists in API response
                    if (!hasPersonalInfo && !hasImages && !hasOtherDocs) {
                        return null;
                    }

                    // Sub-component for text diffs
                    const ProfileDiffCard = ({ label, oldVal, newVal }) => {
                        if (!isValid(oldVal) && !isValid(newVal)) return null;
                        const changed = String(oldVal ?? "") !== String(newVal ?? "");
                        return (
                            <div
                                className={`rounded-lg p-3 border ${
                                    changed ? "border-blue-200 bg-blue-50/40" : "border-gray-100 bg-gray-50/50"
                                }`}
                            >
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    {label}
                                </p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-xs text-gray-400 block">
                                            {t("old")}
                                        </span>
                                        <span className="font-medium text-gray-700 break-words">
                                            {isValid(oldVal) ? String(oldVal) : "—"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-xs text-blue-600 block font-medium">
                                        {t("new")}
                                        </span>
                                        <span className="font-semibold text-gray-900 break-words">
                                            {isValid(newVal) ? String(newVal) : "—"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    };

                    // Single image tile
                    const ImageTile = ({ src, label, variant }) => {
                        const isNew = variant === "new";
                        return (
                            <div className="flex-1 min-w-0">
                                <span
                                    className={`text-xs block mb-2 ${
                                        isNew ? "text-blue-600 font-medium" : "text-gray-400"
                                    }`}
                                >
                                    {/* {isNew ? "New" : "Old"} */}
                                    {/* {isNew ? t("new", "New") : t("old", "Old")} */}
                                    {isNew ? t("new") : t("old")}
                                </span>
                                {isValid(src) ? (
                                    <button
                                        type="button"
                                        onClick={() => openDocument(src)}
                                        className={`group relative block w-full aspect-[4/3] overflow-hidden rounded-lg border bg-white shadow-sm ${
                                            isNew ? "border-blue-300" : "border-gray-200"
                                        }`}
                                    >
                                        <img
                                            src={src}
                                            // alt={`${isNew ? "New" : "Old"} ${label}`}
                                            alt={`${isNew ? t("new") : t("old")} ${label}`}
                                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition">
                                            <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/60 px-3 py-1 rounded-full transition">
                                                {t("click_to_view")}
                                            </span>
                                        </span>
                                    </button>
                                ) : (
                                    <div className="w-full aspect-[4/3] rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-xs text-gray-400 italic">
                                        {t("no_image")}
                                    </div>
                                )}
                            </div>
                        );
                    };

                    // Sub-component for image diffs
                    const ImageDiffCard = ({ label, oldImg, newImg }) => {
                        if (!isValid(oldImg) && !isValid(newImg)) return null;
                        return (
                            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                    {label}
                                </p>
                                <div className="flex gap-3">
                                    <ImageTile src={oldImg} label={label} variant="old" />
                                    <ImageTile src={newImg} label={label} variant="new" />
                                </div>
                            </div>
                        );
                    };

                    return (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Header */}
                            <div className="p-6 md:p-8 bg-gray-50 border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {t("profile_update_request")}
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {t("review_customer_profile_changes")}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 md:p-8 space-y-8">
                                {/* PERSONAL INFORMATION */}
                                {hasPersonalInfo && (
                                    <div>
                                        {/* <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                            {t("personal_information")}
                                        </h3> */}
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                            {t("personal_information")}
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {textFields.map(([key, label]) => (
                                                <ProfileDiffCard
                                                    key={key}
                                                    label={label}
                                                    oldVal={oldData[key]}
                                                    newVal={updatedData[key]}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* DOCUMENTS & IMAGES */}
                                {(hasImages || hasOtherDocs) && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                            {t("documents_and_images")}
                                        </h3>

                                        {hasImages && (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                                {imageFields.map(([key, label]) => (
                                                    <ImageDiffCard
                                                        key={key}
                                                        label={label}
                                                        oldImg={oldData[key]}
                                                        newImg={updatedData[key]}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        {/* OTHER DOCUMENTS */}
                                        {hasOtherDocs && (
                                            <div className={hasImages ? "mt-8" : ""}>
                                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                                    Other Documents
                                                </h4>
                                                <div className="space-y-4">
                                                    {updatedData.other_documents.map((doc) => {
                                                        const oldDoc = oldData.other_documents?.[doc.id];
                                                        return (
                                                            <div key={doc.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                                                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                                                                    {doc.document_name}
                                                                </p>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                                                        <p className="text-xs font-semibold text-gray-400 uppercase">
                                                                            Old Document
                                                                        </p>
                                                                        {oldDoc?.file_path ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openDocument(oldDoc.file_path)}
                                                                                className="text-sm font-medium text-blue-600 hover:underline mt-2 text-left break-all block"
                                                                            >
                                                                                {oldDoc.document_name || "View Old Document"}
                                                                            </button>
                                                                        ) : (
                                                                            <p className="text-sm text-gray-400 mt-1 italic">—</p>
                                                                        )}
                                                                    </div>

                                                                    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
                                                                        <p className="text-xs font-semibold text-blue-600 uppercase">
                                                                            New Document
                                                                        </p>
                                                                        {doc.file_path ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openDocument(doc.file_path)}
                                                                                className="text-sm font-medium text-blue-600 hover:underline mt-2 text-left break-all block"
                                                                            >
                                                                                {doc.document_name || "View New Document"}
                                                                            </button>
                                                                        ) : (
                                                                            <p className="text-sm text-gray-400 mt-1 italic">—</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* BUTTONS */}
                                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => handleProfileUpdateAction("rejected")}
                                        className="px-5 py-2.5 rounded-lg border border-red-300 bg-white text-red-600 hover:bg-red-50 text-sm font-medium transition"
                                    >
                                        {t("reject")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleProfileUpdateAction("approved")}
                                        className="px-5 py-2.5 rounded-lg bg-black text-white hover:bg-gray-800 text-sm font-medium transition"
                                    >
                                        {t("approve")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
        {/* for the old/new data of the customer ends here */}

            {isModalOpen && (
                <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setIsModalOpen} />
            )}
        </div>
    );
}

export default ViewCustomer;